/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { CertModel, CertsApiService, LbErrorService } from '../../../../../shared';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LanguageService,
    LogService,
    ModalService,
    NotifyService,
    UtilsService
} from '../../../../../../../core/services';
import { Observable } from 'rxjs';
import { nameValidation } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-new-certs',
    templateUrl: './uploadPemPfx.component.html',
    styleUrls: ['../../../../../../../../assets/shared/scss/embedded.scss']
})

export class UploadPemPfxComponent extends BaseComponent implements OnInit {
    /**
     * Embedded in wizard flag.
     */
    public componentEmbedded: boolean = false;

    /**
     * Store the id passed in from parent (in this case the CSR id to apply to a certificate).
     */
    public id: string;

    /**
     * Form group reference
     */
    public uploadForm: FormGroup;

    /**
     * Hide the password field group; only show if file extension is pfx
     */
    public hidePfxPasswordFieldGroup: boolean = true;

    /**
     * State of the file input.
     */
    public inputReset: boolean = false;

    /**
     * Store the callback function passed in from parent component.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store the page title.
     */
    public pageTitle: string;

    /**
     * We don't always get an error object; so we store if an error condition has been thrown.
     * @private
     */
    private hasErrorBeenTriggered: boolean = false;

    /**
     * Store certs' collection.
     * @private
     */
    private certs: CertModel[];

    constructor(
        private certsService: CertsApiService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.getCerts();
        this.setUpForm();
        this.setUpFormSubscribers();
    }

    public ngOnInit(): void {
        this.id = this.componentEmbedded ? this.id : this.route.snapshot.params['id'];
        this.pageTitle = this.id ? 'certificate_apply_csr' : 'upload_pem_pfx';
    }

    /**
     * Return to parent component.
     */
    public returnToParent(): void {
        if (this.componentEmbedded) {
            this.callBack();
        } else {
            this.router.navigate([this.id ? '../../../' : '../../'], { relativeTo: this.route }).then(() => {
            });
        }
    }

    /**
     * Handle file
     * @param event
     */
    public onFileChange(event: any): any {
        this.logService.debug('onFileChange', 'start');
        if (event.target.files?.length) {
            const file = event.target.files[0];
            const extension = this.utilsService.getExtensionFromFilename(file.name);
            this.hidePfxPasswordFieldGroup = extension !== 'pfx';
            if (extension !== 'pem' && extension !== 'pfx') {
                this.uploadForm.get('file').setErrors({
                    'invalidFileType':
                        `${this.languageService.translate('invalid_file_type')}`
                });
                this.uploadForm.get('file').markAsTouched();

                return;
            }
            const reader: FileReader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.uploadForm.patchValue({
                    file: reader.result,
                    fileName: file.name
                });
            };
        }
    }

    /**
     * Submit uploaded certificate.
     * @param force
     */
    public submitCertUpload(force?: boolean): Observable<any> {
        return new Observable((observer) => {
            const { extension, base64result } = this.fileHasValidParts();
            if (extension && base64result) {
                let cert = this.getFormattedCertificate(extension, base64result);
                if (this.id) {
                    cert = this.formatCertificateForApply(cert);
                }

                if (force) {
                    cert['force'] = true;
                }
                const apiCall = this.id ? this.certsService.applyCSR(cert) : this.certsService.addCert(cert);
                apiCall
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (): void => {
                            this.returnToParent();
                            observer.next();
                            observer.complete();
                        },
                        error: (error: any): void => {
                            if (error?.length) {
                                this.reportCertUploadError(error);
                            } else {
                                this.hasErrorBeenTriggered = true;
                                this.uploadForm.get('file')
                                    .setErrors({
                                        'UploadPemPfxComponent: unknown error':
                                            `${this.languageService.translate('invalid_certificate')}`
                                    });
                                this.uploadForm.markAllAsTouched();
                            }

                            this.checkCertExpired(error.error?.errors);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            } else {
                observer.error();
                observer.complete();
                this.notifyService.error('Error uploading certificate');
            }
        });
    }

    /**
     * Cancel upload
     */
    public closeUploadModal(): void {
        this.modalService.close('force-upload-cert');
    }

    /**
     * If any errors are encountered during the cert upload then report them.
     * @param formattedErrors
     */
    public reportCertUploadError(formattedErrors: any): void {
        for (const formattedError of formattedErrors.error.errors) {
            if (formattedError.field === 'pem' || formattedError.field === 'pfx' || formattedError.field === 'certsWithPFX') {
                formattedError.field = 'file';
            }
        }
        this.lbErrorService.pageWithErrors(this.uploadForm, formattedErrors.error.errors);
    }

    /**
     * Check if certificate has expired and display force upload modal.
     * @param errors
     * @private
     */
    private checkCertExpired(errors: any[]): void {
        let certExpired: boolean = false;
        for (const error of errors) {
            if (error.message?.includes('expired')) {
                certExpired = true;
                break;
            }
        }

        if (certExpired) {
            this.modalService.openModalInParent('force-upload-cert');
        } else {
            this.modalService.close('force-upload-cert');
        }
    }

    /**
     * Get certs.
     * @private
     */
    private getCerts(): void {
        this.certsService.getCerts()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: CertModel[]): void => {
                    this.certs = response;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UploadPemPfxComponent: getCerts() failed to fetch certs ', error.message);
                }
            });
    }

    /**
     * Check file is valid.
     * @private
     */
    private fileHasValidParts(): any {
        const regex = /(?:\.([^.]+))?$/;
        const fileNameExec = regex.exec(this.uploadForm.value.fileName);
        if (!fileNameExec || !fileNameExec.length) {
            this.uploadForm.get('file')
                .setErrors({
                    'UploadPemPfxComponent: getFormattedCertificate':
                        `${this.languageService.translate('file_extension_not_found')}`
                });
            this.uploadForm.get('file').markAsTouched();

            return {};
        }

        const base64Match = this.uploadForm.value.file.match(/^data:.+\/(.+);base64,(.*)$/);
        if (!base64Match || !base64Match.length || base64Match.length < 2 || (base64Match[2] && !base64Match[2].length)) {
            this.uploadForm.get('file')
                .setErrors({ 'UploadPemPfxComponent: getFormattedCertificate': `${this.languageService.translate('base_64_not_found')}` });
            this.uploadForm.get('file').markAsTouched();

            return {};
        }

        const extension = this.utilsService.getExtensionFromFilename(fileNameExec.input);
        const base64result = base64Match[2];

        return { extension, base64result };
    }

    /**
     * Get formatted cert object.
     * @param extension
     * @param base64result
     * @private
     */
    private getFormattedCertificate(extension: string, base64result: any): any {
        const cert: any = {
            name: this.uploadForm.value.name,
        };

        if (extension === 'pem') {
            cert.pem = atob(base64result);
        } else if (extension === 'pfx') {
            cert.pfx = base64result;
            cert.password = this.uploadForm.get('password').value;
        } else {
            this.uploadForm.get('file').setErrors({
                'invalidFileType': `${this.languageService.translate('invalid_file_type')}`
            });
            this.uploadForm.get('file').markAsTouched();

            return;
        }

        return cert;
    }

    /**
     * Format the certificate if applying a CSR.
     * @param cert
     * @private
     */
    private formatCertificateForApply(cert: any): any {
        cert['id'] = this.id;
        cert['cert'] = cert.pem;
        delete cert.pem;

        return cert;
    }

    /**
     * Set-up form.
     * @private
     */
    private setUpForm(): void {
        this.uploadForm = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            file: [null, Validators.required],
            password: [null],
            fileName: []
        });
    }

    /**
     * Set-up form subscribers.
     * TODO: We don't get anything meaningful from the API when an incorrect password is given.
     * All we can do at the moment (until the API is updated) is check for an error and reset
     * the file field for errors.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.uploadForm.get('name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('name', () => {
                        this.utilsService.validateCertsFormLabel(this.uploadForm.get('name'), this.certs);
                    }, 250);
                }
            });

        this.uploadForm.get('password').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('password', () => {
                        // We don't always get an error object; so we check if an error condition has been triggered.
                        if (this.hasErrorBeenTriggered) {
                            this.uploadForm.get('file').setErrors(null);
                            this.uploadForm.markAllAsTouched();
                            this.hasErrorBeenTriggered = false;
                        }
                    }, 250);
                }
            });
    }
}
